/**
 * SharesPanel — the OWNER's list of everyone this estimate has been sent to.
 *
 * One row per location: kind, where, when it was sent, whether it was opened,
 * how many replies, the send outcome, and the actions — resend, promote / demote
 * (trusted installers only), remove. The owner never sees a recipient's address,
 * only a masked form; the location was picked from a public directory.
 */

import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Send, Store, HardHat, RefreshCw, ShieldCheck, ShieldOff, Trash2, Loader2, Eye, EyeOff, MessageSquare, AlertTriangle } from 'lucide-react';
import type { ShareLimits, ShareRecipient } from '../../types/share';
import { demoteShare, promoteShare, removeShare, resendShare } from '../../services/shareApi';
import './SharesPanel.css';

interface Props {
  estimateGuid: string;
  shares: ShareRecipient[];
  limits: ShareLimits | null;
  enabled: boolean;
  authed: boolean;
  onShareClick: () => void;
  onChanged: () => Promise<void> | void;
  onNotice: (message: string, variant: 'success' | 'error') => void;
}

const fmtDate = (s: string | null) => (s ? new Date(s.replace(' ', 'T')).toLocaleDateString() : '');

export const SharesPanel: React.FC<Props> = ({ estimateGuid, shares, limits, enabled, authed, onShareClick, onChanged, onNotice }) => {
  const { t } = useTranslation('results');
  const [busy, setBusy] = useState<number | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<ShareRecipient | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ShareRecipient | null>(null);
  const active = shares.filter(s => s.status === 'active');
  const removed = shares.filter(s => s.status !== 'active');
  const retailers = active.filter(s => s.kind === 'retailer');
  const canAdd = enabled && (!limits || limits.remainingTotal > 0);

  const run = async (share: ShareRecipient, fn: () => Promise<{ ok: boolean; status?: string; error?: string | null }>, okMsg: string) => {
    setBusy(share.id);
    try {
      const r = await fn();
      if (r.ok || r.status === 'removed') onNotice(okMsg, 'success');
      else if (r.status === 'saved_not_sent') onNotice(t('share.panel.notices.savedNotSent'), 'error');
      else onNotice(r.error || t('share.panel.notices.failed'), 'error');
      await onChanged();
    } catch (e) {
      const err = e as Error & { status?: number };
      onNotice(err.status === 429 ? t('share.panel.notices.cooldown') : (err.message || t('share.panel.notices.failed')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const scrollToThread = (id: number) => {
    document.getElementById(`estimate-comments-thread-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const KindIcon: React.FC<{ kind: ShareRecipient['kind'] }> = ({ kind }) => kind === 'retailer' ? <Store size={14} /> : <HardHat size={14} />;

  const statusBadge = (s: ShareRecipient) => {
    if (s.lastSendStatus === 'ok') return <span className="badge text-bg-success">{s.redirected ? t('share.panel.status.redirected') : t('share.panel.status.sent')}</span>;
    if (s.lastSendStatus === 'failed') return <span className="badge text-bg-danger">{t('share.panel.status.failed')}</span>;
    if (s.lastSendStatus === 'skipped') return <span className="badge text-bg-warning text-dark">{t('share.panel.status.notSent')}</span>;
    return <span className="badge text-bg-secondary">{t('share.panel.status.queued')}</span>;
  };

  return (
    <section className="shares-panel d-print-none print-hide" aria-labelledby="shares-panel-title">
      <div className="shares-panel__head">
        <h2 id="shares-panel-title" className="section-title">
          <span className="section-title-icon"><Send size={22} /></span>
          {t('share.panel.title')}
          {active.length > 0 && <span className="shares-panel__count">{active.length}</span>}
        </h2>
        {canAdd && (
          <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2" onClick={onShareClick}>
            <Send size={14} /> {active.length === 0 ? t('share.panel.sendFirst') : t('share.panel.addAnother')}
          </button>
        )}
      </div>
      <p className="section-description">{t('share.panel.description')}</p>

      {active.length === 0 ? (
        <div className="shares-panel__empty">
          <p>{enabled ? t('share.panel.empty') : t('share.panel.disabled')}</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-bordered shares-table mb-0">
              <thead>
                <tr>
                  <th>{t('share.panel.columns.recipient')}</th>
                  <th>{t('share.panel.columns.sent')}</th>
                  <th>{t('share.panel.columns.viewed')}</th>
                  <th className="text-center">{t('share.panel.columns.comments')}</th>
                  <th>{t('share.panel.columns.status')}</th>
                  <th className="text-end">{t('share.panel.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {active.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div className="shares-table__name"><KindIcon kind={s.kind} /> {s.locationName}
                        {s.promoted && <span className="badge text-bg-info ms-2">{t('share.panel.promotedBadge')}</span>}
                      </div>
                      <div className="text-muted small">{[s.city, s.region].filter(Boolean).join(', ')}{s.emailMasked && <> · {s.emailMasked}</>}</div>
                    </td>
                    <td className="small text-nowrap">{fmtDate(s.sentAt)}{s.sendCount > 1 && <div className="text-muted">{t('share.panel.resentTimes', { count: s.sendCount - 1 })}</div>}</td>
                    <td className="small">
                      {s.firstViewedAt
                        ? <span className="text-success d-inline-flex align-items-center gap-1"><Eye size={14} /> {fmtDate(s.firstViewedAt)}</span>
                        : <span className="text-muted d-inline-flex align-items-center gap-1"><EyeOff size={14} /> {t('share.panel.notViewed')}</span>}
                    </td>
                    <td className="text-center">
                      {s.commentCount > 0
                        ? <button type="button" className="btn btn-link btn-sm p-0 d-inline-flex align-items-center gap-1" onClick={() => scrollToThread(s.id)}><MessageSquare size={14} /> {s.commentCount}</button>
                        : <span className="text-muted">0</span>}
                    </td>
                    <td>{statusBadge(s)}</td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-outline-secondary btn-sm me-1" title={t('share.panel.actions.resend')} aria-label={t('share.panel.actions.resend')}
                        disabled={busy === s.id} onClick={() => void run(s, () => resendShare(estimateGuid, s.id, authed), t('share.panel.notices.resent'))}>
                        {busy === s.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      </button>
                      {s.kind === 'installer' && (
                        s.promoted
                          ? <button type="button" className="btn btn-outline-info btn-sm me-1" title={t('share.panel.actions.demote')} aria-label={t('share.panel.actions.demote')} disabled={busy === s.id}
                              onClick={() => void run(s, () => demoteShare(estimateGuid, s.id, authed), t('share.panel.notices.demoted'))}><ShieldOff size={14} /></button>
                          : <button type="button" className="btn btn-outline-info btn-sm me-1" title={t('share.panel.actions.promote')} aria-label={t('share.panel.actions.promote')} disabled={busy === s.id || retailers.length === 0}
                              onClick={() => setPromoteTarget(s)}><ShieldCheck size={14} /></button>
                      )}
                      <button type="button" className="btn btn-outline-danger btn-sm" title={t('share.panel.actions.remove')} aria-label={t('share.panel.actions.remove')} disabled={busy === s.id} onClick={() => setRemoveTarget(s)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="d-md-none shares-cards">
            {active.map(s => (
              <div className="shares-card" key={s.id}>
                <div className="shares-card__title"><KindIcon kind={s.kind} /> {s.locationName} {s.promoted && <span className="badge text-bg-info">{t('share.panel.promotedBadge')}</span>}</div>
                <div className="text-muted small">{[s.city, s.region].filter(Boolean).join(', ')}</div>
                <div className="shares-card__meta">
                  <span>{t('share.panel.columns.sent')}: {fmtDate(s.sentAt)}</span>
                  <span>{s.firstViewedAt ? t('share.panel.viewedOn', { date: fmtDate(s.firstViewedAt) }) : t('share.panel.notViewed')}</span>
                  <span><MessageSquare size={12} /> {s.commentCount}</span>
                  {statusBadge(s)}
                </div>
                <div className="shares-card__actions">
                  <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy === s.id} onClick={() => void run(s, () => resendShare(estimateGuid, s.id, authed), t('share.panel.notices.resent'))}><RefreshCw size={14} /> {t('share.panel.actions.resend')}</button>
                  {s.kind === 'installer' && (s.promoted
                    ? <button type="button" className="btn btn-outline-info btn-sm" disabled={busy === s.id} onClick={() => void run(s, () => demoteShare(estimateGuid, s.id, authed), t('share.panel.notices.demoted'))}><ShieldOff size={14} /> {t('share.panel.actions.demote')}</button>
                    : <button type="button" className="btn btn-outline-info btn-sm" disabled={busy === s.id || retailers.length === 0} onClick={() => setPromoteTarget(s)}><ShieldCheck size={14} /> {t('share.panel.actions.promote')}</button>)}
                  <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy === s.id} onClick={() => setRemoveTarget(s)}><Trash2 size={14} /> {t('share.panel.actions.remove')}</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {removed.length > 0 && (
        <details className="shares-panel__removed">
          <summary>{t('share.panel.removedSummary', { count: removed.length })}</summary>
          <ul>
            {removed.map(s => <li key={s.id}><KindIcon kind={s.kind} /> {s.locationName} <span className="text-muted small">— {t('share.panel.removedOn', { date: fmtDate(s.removedAt) })}</span></li>)}
          </ul>
        </details>
      )}

      {limits && limits.remainingTotal === 0 && (
        <p className="small text-muted mt-2 d-flex align-items-center gap-1"><AlertTriangle size={14} /> {t('share.panel.limitReached')}</p>
      )}

      {/* ---- Promote picker ---- */}
      {promoteTarget && (
        <PromotionPicker
          share={promoteTarget}
          retailers={retailers}
          busy={busy === promoteTarget.id}
          onCancel={() => setPromoteTarget(null)}
          onConfirm={(targets) => {
            const s = promoteTarget;
            setPromoteTarget(null);
            void run(s, () => promoteShare(estimateGuid, s.id, targets, authed), t('share.panel.notices.promoted'));
          }}
        />
      )}

      {/* ---- Remove confirm ---- */}
      {removeTarget && (
        <ConfirmModal
          title={t('share.remove.title')}
          body={<Trans i18nKey="share.remove.body" ns="results" values={{ name: removeTarget.locationName }} components={{ b: <strong /> }} />}
          confirmLabel={t('share.remove.confirm')}
          busy={busy === removeTarget.id}
          danger
          onCancel={() => setRemoveTarget(null)}
          onConfirm={() => {
            const s = removeTarget;
            setRemoveTarget(null);
            void run(s, () => removeShare(estimateGuid, s.id, authed), t('share.panel.notices.removed'));
          }}
        />
      )}
    </section>
  );
};

/* ------------------------------------------------------------------------ */

const ConfirmModal: React.FC<{
  title: string; body: React.ReactNode; confirmLabel: string; busy: boolean; danger?: boolean;
  onCancel: () => void; onConfirm: () => void; children?: React.ReactNode;
}> = ({ title, body, confirmLabel, busy, danger, onCancel, onConfirm, children }) => {
  const { t } = useTranslation('results');
  return (
    <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">{title}</h5>
            <button type="button" className="btn-close" aria-label={t('auth:close')} onClick={onCancel} disabled={busy} />
          </div>
          <div className="modal-body">
            <p className="mb-0">{body}</p>
            {children}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>{t('auth:cancel')}</button>
            <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'} d-inline-flex align-items-center gap-2`} onClick={onConfirm} disabled={busy}>
              {busy && <Loader2 size={16} className="animate-spin" />} {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PromotionPicker: React.FC<{
  share: ShareRecipient; retailers: ShareRecipient[]; busy: boolean;
  onCancel: () => void; onConfirm: (targets: 'all' | number[]) => void;
}> = ({ share, retailers, busy, onCancel, onConfirm }) => {
  const { t } = useTranslation('results');
  const [mode, setMode] = useState<'all' | 'selected'>('all');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const toggle = (id: number) => setPicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <ConfirmModal
      title={t('share.promote.title', { name: share.locationName })}
      body={<Trans i18nKey="share.promote.body" ns="results" values={{ name: share.locationName }} components={{ b: <strong /> }} />}
      confirmLabel={t('share.promote.save')}
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(mode === 'all' ? 'all' : Array.from(picked))}
    >
      <div className="mt-3">
        <div className="form-check">
          <input className="form-check-input" type="radio" id="promote-all" checked={mode === 'all'} onChange={() => setMode('all')} />
          <label className="form-check-label" htmlFor="promote-all">{t('share.promote.all')}</label>
        </div>
        <div className="form-check">
          <input className="form-check-input" type="radio" id="promote-selected" checked={mode === 'selected'} onChange={() => setMode('selected')} />
          <label className="form-check-label" htmlFor="promote-selected">{t('share.promote.selected')}</label>
        </div>
        {mode === 'selected' && (
          <div className="ms-4 mt-2 d-flex flex-column gap-1">
            {retailers.map(r => (
              <div className="form-check" key={r.id}>
                <input className="form-check-input" type="checkbox" id={`promote-r-${r.id}`} checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
                <label className="form-check-label" htmlFor={`promote-r-${r.id}`}>{r.locationName} <span className="text-muted small">{[r.city, r.region].filter(Boolean).join(', ')}</span></label>
              </div>
            ))}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
};

export default SharesPanel;
